import type { PopoutPosition, SoftphonePopoutProps } from './index.types';
import { useDraggablePosition } from 'Hooks/draggablePosition';
import { observer } from 'mobx-react';
import { isTerminalState } from 'Modules/softphone/constants';
import { useSoftphoneStore } from 'Modules/softphone/store';
import type { CallSession } from 'Modules/softphone/types';
import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Rnd } from 'react-rnd';
import { SoftphoneUI } from '../SoftphoneUI';
import { DraggableHeader } from './DraggableHeader';
import {
  SOFTPHONE_POPOUT_MIN_HEIGHT,
  SOFTPHONE_POPOUT_STORAGE_KEY,
  SOFTPHONE_POPOUT_WIDTH,
  SOFTPHONE_POPOUT_BOTTOM_SAFE_AREA,
} from './index.constants';

/**
 * Get default position dynamically based on current window dimensions.
 */
function getDefaultPosition(): PopoutPosition {
  return {
    x: Math.max(0, window.innerWidth - SOFTPHONE_POPOUT_WIDTH - 70),
    y: 80,
  };
}

/**
 * Check if a call is in an active state (not terminal)
 * Terminal states (idle, ended, failed, busy, no-answer) should close the UI immediately
 */
const isCallActive = (call: CallSession): boolean => {
  return !isTerminalState(call.state);
};

/**
 * SoftphonePopout - Draggable popout window for the softphone
 *
 * Wraps SoftphoneUI in a draggable container using react-rnd.
 * Persists position to localStorage and handles window resize.
 * Manages its own visibility based on active calls and dialpad state.
 *
 * Closes immediately when call enters terminal state to prevent
 * visual flash of avatar/state changes during cleanup.
 */
export const SoftphonePopout: React.FC<SoftphonePopoutProps> = observer(
  ({ open, onClose, defaultPosition, isActiveCall, ...softphoneProps }) => {
    // Use softphone store for dialpad state and active calls
    const { isDialpadActive, setDialpadActive, activeCalls, activeConference } =
      useSoftphoneStore((state) => ({
        isDialpadActive: state.isDialpadActive,
        setDialpadActive: state.setDialpadActive,
        activeCalls: state.activeCalls,
        activeConference: state.activeConference,
      }));
    const hasActiveCalls =
      isActiveCall ?? Array.from(activeCalls.values()).some(isCallActive);
    const hasActiveConference = activeConference !== null;
    // Calculate default position dynamically based on current window size
    const computedDefaultPosition = useMemo<PopoutPosition>(
      (): PopoutPosition => defaultPosition ?? getDefaultPosition(),
      [defaultPosition]
    );

    const isSoftphoneVisible = open ?? (hasActiveCalls || hasActiveConference || isDialpadActive);

    // Show close button when dialpad is open (available in idle state and during active calls per FR1)
    const showDialpad = isDialpadActive;

    const { position, onDragStop } = useDraggablePosition({
      storageKey: SOFTPHONE_POPOUT_STORAGE_KEY,
      defaultPosition: computedDefaultPosition,
      width: SOFTPHONE_POPOUT_WIDTH,
      minHeight: SOFTPHONE_POPOUT_MIN_HEIGHT,
    });

    // Handler to close the dialpad
    const handleCloseDialpad = () => {
      setDialpadActive(false);
    };

    if (!isSoftphoneVisible) {
      return null;
    }

    const maxHeight = `calc(100vh - ${position.y + SOFTPHONE_POPOUT_BOTTOM_SAFE_AREA}px)`;

    return createPortal(
      <Rnd
        bounds="window"
        position={position}
        size={{ width: SOFTPHONE_POPOUT_WIDTH, height: 'auto' }}
        dragHandleClassName="softphone-drag-handle"
        className="fixed z-[9999] bg-background shadow-2xl border border-border overflow-hidden rounded-[8px]"
        onDragStop={onDragStop}
        enableResizing={false}
        allowAnyClick={false}
      >
        <div className="flex flex-col bg-background" style={{ maxHeight }}>
          <DraggableHeader
            dragHandleClassName="softphone-drag-handle"
            showCloseButton={showDialpad}
            onClose={handleCloseDialpad}
          />

          <div className="overflow-y-auto scroll-area-content flex-1 min-h-0">
            <SoftphoneUI {...softphoneProps} variant="popup" />
          </div>
        </div>
      </Rnd>,
      document.body
    );
  }
);
